import { useEffect, useState } from 'react'

export interface TimeLeft {
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
}

function getTimeLeft(deadline: number): TimeLeft {
  const diff = deadline - Date.now()
  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, isExpired: true }
  }
  const totalSeconds = Math.floor(diff / 1000)
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    isExpired: false,
  }
}

/**
 * Counts down to a fixed, real deadline (ISO string). The value is derived from
 * the clock on every tick, so refreshing the page never restarts the timer.
 *
 * Pass `active: false` while the timer is off-screen: the interval stops, so it
 * never re-renders — or competes with the hero image — during the initial load.
 */
export function useCountdown(deadlineIso: string, active = true): TimeLeft {
  const deadline = new Date(deadlineIso).getTime()
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => getTimeLeft(deadline))

  useEffect(() => {
    if (!active) return
    if (getTimeLeft(deadline).isExpired) return

    setTimeLeft(getTimeLeft(deadline))
    const interval = window.setInterval(
      () => setTimeLeft(getTimeLeft(deadline)),
      1000,
    )
    return () => window.clearInterval(interval)
  }, [deadline, active])

  return timeLeft
}
